// Saved Lists persist their column layout (order + widths) in the DB, but the
// useColumnResizeAndReorder hook only reads/writes layout through sessionStorage
// (keys `${storageKey}-col-widths` and `${storageKey}-col-order`). These helpers
// bridge the two: sessionStorage stays the transient channel into/out of the hook,
// while the DB (FilterList.params) remains the source of truth.

const WIDTHS_SUFFIX = "-col-widths";
const ORDER_SUFFIX = "-col-order";

// A saved list gets its own scoped key so opening it never overwrites the shared
// main-view layout, and each list keeps its own layout.
export const getColumnLayoutStorageKey = (viewKey, filterListId) =>
  filterListId ? `${viewKey}__sl-${filterListId}` : viewKey;

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

// Shape-guard the parsed values: widths must be a {colKey: finite number} map and
// order an array of column-key strings, so a malformed/crafted sessionStorage value
// can never reach the table config.
const parseWidths = (value) => {
  const parsed = safeParse(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
  return Object.fromEntries(Object.entries(parsed).filter(([, width]) => typeof width === "number" && isFinite(width)));
};

const parseOrder = (value) => {
  const parsed = safeParse(value);
  return Array.isArray(parsed) && parsed.every((key) => typeof key === "string") ? parsed : null;
};

// Read the hook's live layout from sessionStorage — used when saving a list.
export const readColumnLayoutFromSession = (storageKey) => ({
  columnWidths: parseWidths(sessionStorage.getItem(`${storageKey}${WIDTHS_SUFFIX}`)),
  columnOrder: parseOrder(sessionStorage.getItem(`${storageKey}${ORDER_SUFFIX}`))
});

// Clears a list's layout so a table remount re-seeds to defaults (Restore default columns).
export const resetColumnLayoutInSession = (storageKey) => {
  sessionStorage.removeItem(`${storageKey}${WIDTHS_SUFFIX}`);
  sessionStorage.removeItem(`${storageKey}${ORDER_SUFFIX}`);
};

// Write a saved layout into sessionStorage so the hook restores it on mount —
// used when loading a list.
export const hydrateColumnLayoutToSession = (storageKey, { columnWidths, columnOrder } = {}) => {
  sessionStorage.setItem(`${storageKey}${WIDTHS_SUFFIX}`, JSON.stringify(columnWidths ?? {}));
  if (columnOrder?.length) {
    sessionStorage.setItem(`${storageKey}${ORDER_SUFFIX}`, JSON.stringify(columnOrder));
  } else {
    sessionStorage.removeItem(`${storageKey}${ORDER_SUFFIX}`);
  }
};
